import { BusyContent, LoadingState, EmptyState, PageHeader } from '../components/ui/Primitives';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CRMIntegrations.css';
import { logApiError, logEvent, logSectionView } from '../utils/logger';
import { getBackendHost } from '../utils/backendHost';
import CalendarIntegrations from '../components/CalendarIntegrations';
import IntegrationProviderIcon from '../components/IntegrationProviderIcon';
import { rememberOnboardingOAuth, returnToOnboarding } from '../utils/onboardingOAuth';

const PROVIDER_CONTENT = {
  pipedrive: {
    eyebrow: 'Customer relationship management',
    steps: [
      'Connect the Pipedrive company account you want Sayable to use.',
      'Approve the app permissions to read and write contacts, deals, leads, and their custom fields.',
      'Create a receptionist workflow and choose a person, organization, deal, or lead as the record type.',
      'Map intake answers and required fields. Leads must reference an existing Pipedrive person or organization ID, or use Contact fields to find or create a person with Contacts: Full access.',
    ],
  },
  zoho: {
    eyebrow: 'Customer relationship management',
    steps: [
      'Connect using a Zoho CRM account that can create and update your destination records.',
      'Sign in to Zoho and choose the CRM organization or test environment you want Sayable to use.',
      'Approve record access and permission to read modules, fields, layouts, and account details.',
      'Create a receptionist workflow, select a module, and map intake answers to its fields. Sayable uses the Standard layout when available.',
    ],
  },
  salesforce: {
    eyebrow: 'Customer relationship management',
    steps: [
      'Connect using a Salesforce administrator who can approve external applications.',
      'Sign in to the Salesforce organization you want Sayable to use.',
      'Approve the requested CRM permissions.',
      'Create receptionist workflows that use this account as their outcome.',
    ],
  },
  hubspot: {
    eyebrow: 'Customer relationship management',
    steps: [
      'Connect using a HubSpot administrator who has permission to connect apps.',
      'Sign in to HubSpot and select the account you want to connect.',
      'Approve the requested CRM permissions for Sayable.',
      'Create receptionist workflows that use this account as their outcome.',
    ],
  },
  dynamics365: {
    eyebrow: 'Customer relationship management',
    steps: [
      'Connect using a Microsoft account that can access your Dynamics 365 environment.',
      'Approve delegated access so Sayable.ai can work with the records that account can access.',
      'Choose the Dynamics 365 environment, record type, and fields for each receptionist workflow.',
      'Publish the workflow to create or update matching customer records after conversations.',
    ],
  },
  asana: {
    eyebrow: 'Project and task management',
    steps: [
      'Connect the Asana account that can access your destination workspaces.',
      'Approve workspace, team, user, project, task, and custom-field read permissions for Sayable.',
      'Choose a workspace and create a custom workflow for an Asana task or project.',
      'Select the project, team, and optional task assignee used by that workflow.',
    ],
  },
};

const OAUTH_ERROR_MESSAGES = {
  PIPEDRIVE_TOKEN_EXCHANGE_FAILED: 'Pipedrive rejected the connection request. Start a new attempt.',
  PIPEDRIVE_TOKEN_EXCHANGE_TIMEOUT: 'Pipedrive did not respond in time. Try again shortly.',
  PIPEDRIVE_UNAVAILABLE: 'Pipedrive could not be reached. Try again shortly.',
  PIPEDRIVE_REFRESH_TOKEN_MISSING: 'Pipedrive did not return a refresh token. Reconnect the account.',
  PIPEDRIVE_IDENTITY_MISSING: 'Pipedrive did not return the authorized company and user. Try reconnecting.',
  ZOHO_TOKEN_EXCHANGE_FAILED: 'Zoho rejected the connection request. Start a new attempt.',
  ZOHO_TOKEN_EXCHANGE_TIMEOUT: 'Zoho did not respond in time. Try again shortly.',
  ZOHO_UNAVAILABLE: 'Zoho could not be reached. Try again shortly.',
  ZOHO_REFRESH_TOKEN_MISSING: 'Zoho did not grant offline access. Reconnect and approve the requested permissions.',
  ZOHO_IDENTITY_MISSING: 'Zoho did not return the authorized account. Check that this login has access to Zoho CRM.',
  OAUTH_DENIED: 'Authorization was denied. Start a new attempt when you are ready.',
  OAUTH_STATE_EXPIRED: 'This connection attempt expired. Start a new attempt to continue.',
  OAUTH_STATE_INVALID: 'This connection attempt is no longer valid. Start a new attempt to continue.',
  OAUTH_STATE_USED: 'This connection attempt has already been completed or retried. Start a new attempt.',
  SALESFORCE_TOKEN_EXCHANGE_FAILED: 'Salesforce rejected the connection request. Start a new attempt.',
  SALESFORCE_TOKEN_EXCHANGE_TIMEOUT: 'Salesforce did not respond in time. Try again shortly.',
  SALESFORCE_UNAVAILABLE: 'Salesforce could not be reached. Try again shortly.',
  HUBSPOT_TOKEN_EXCHANGE_FAILED: 'HubSpot rejected the connection request. Start a new attempt.',
  HUBSPOT_TOKEN_EXCHANGE_TIMEOUT: 'HubSpot did not respond in time. Try again shortly.',
  HUBSPOT_UNAVAILABLE: 'HubSpot could not be reached. Try again shortly.',
  DYNAMICS365_TOKEN_EXCHANGE_FAILED: 'Microsoft rejected the Dynamics 365 connection request. Start a new attempt.',
  DYNAMICS365_TOKEN_TIMEOUT: 'Microsoft did not respond in time. Try again shortly.',
  DYNAMICS365_UNAVAILABLE: 'Microsoft Dynamics 365 could not be reached. Try again shortly.',
  DYNAMICS365_NO_ENVIRONMENTS: 'This Microsoft account cannot access a Dynamics 365 or Dataverse environment.',
  ASANA_TOKEN_EXCHANGE_FAILED: 'Asana rejected the connection request. Start a new attempt.',
  ASANA_TOKEN_EXCHANGE_TIMEOUT: 'Asana did not respond in time. Try again shortly.',
  ASANA_UNAVAILABLE: 'Asana could not be reached. Try again shortly.',
};

function readOAuthResult() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('oauth');
  if (!status) return null;
  return {
    status,
    provider: params.get('provider') || status.split('_')[0],
    code: params.get('code') || '',
    message: params.get('message') || '',
    providerError: params.get('provider_error') || '',
    providerDescription: params.get('provider_description') || '',
    correlationId: params.get('correlation_id') || '',
  };
}

function ProviderMark({ provider }) {
  return <IntegrationProviderIcon provider={provider} className="crm-provider-mark" />;
}

function CRMIntegrations({ checkLogin, orgMember, organization, embedded = false }) {
  const navigate = useNavigate();
  const initialOAuthResult = useMemo(readOAuthResult, []);
  const [providers, setProviders] = useState([]);
  const [providerStatuses, setProviderStatuses] = useState({});
  const [selectedConnectionIds, setSelectedConnectionIds] = useState({});
  const [selectedProviderId, setSelectedProviderId] = useState(() => initialOAuthResult?.provider || new URLSearchParams(window.location.search).get('provider') || '');
  const [loadingAction, setLoadingAction] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activityMessage, setActivityMessage] = useState('');
  const [oauthResult, setOAuthResult] = useState(initialOAuthResult);

  const backendHost = getBackendHost();
  const jwtToken = localStorage.getItem('jwt_authorization');
  const authConfig = useMemo(() => ({ headers: { Authorization: `Bearer ${jwtToken}` } }), [jwtToken]);
  const isAdmin = Boolean(orgMember?.is_org_admin || orgMember?.is_admin);
  const selectedProvider = providers.find(item => item.provider === selectedProviderId);
  const status = providerStatuses[selectedProviderId];
  const connection = status?.connections?.find(item => item.id === selectedConnectionIds[selectedProviderId]) || status?.connection;
  const isConnected = connection?.status === 'connected';
  const providerContent = PROVIDER_CONTENT[selectedProviderId] || {
    eyebrow: 'CRM integration',
    steps: ['Sign in to your CRM.', 'Approve the requested access.', 'Use the account in a receptionist workflow.'],
  };
  const oauthIsError = oauthResult?.status?.endsWith('_error');
  const oauthIsSuccess = oauthResult?.status?.endsWith('_success');
  const oauthProviderName = providers.find(item => item.provider === oauthResult?.provider)?.displayName || oauthResult?.provider || 'CRM';

  const runRequest = useCallback(async (actionName, requestFn) => {
    setLoadingAction(actionName);
    setErrorMessage('');
    try {
      return await requestFn();
    } catch (error) {
      logApiError({ page: 'Integrations', operation: actionName, error });
      const apiError = error.response?.data?.error;
      const providerDescription = apiError?.details?.providerDescription;
      const message = apiError?.message || 'Request failed.';
      setErrorMessage(providerDescription && !message.includes(providerDescription) ? `${message} Provider detail: ${providerDescription}` : message);
      return null;
    } finally {
      setLoadingAction('');
    }
  }, []);

  const fetchProviderStatus = useCallback(async provider => {
    const response = await axios.get(`${backendHost}/v2/integrations/${provider}/status`, authConfig);
    setProviderStatuses(current => ({ ...current, [provider]: response.data }));
    setSelectedConnectionIds(current => {
      const currentIsAvailable = (response.data.connections || []).some(item => (
        item.id === current[provider] && item.status === 'connected'
      ));
      return currentIsAvailable ? current : { ...current, [provider]: response.data.connection?.id || '' };
    });
    return response.data;
  }, [authConfig, backendHost]);

  const fetchProviders = useCallback(async () => {
    await runRequest('providers', async () => {
      const response = await axios.get(`${backendHost}/v2/integrations/providers`, authConfig);
      const availableProviders = (response.data.providers || []).map(provider => (
        provider.provider === 'zoho' ? { ...provider, displayName: 'Zoho' } : provider
      ));
      setProviders(availableProviders);
      if (!selectedProviderId && availableProviders.length === 1) setSelectedProviderId(availableProviders[0].provider);
      const results = await Promise.allSettled(availableProviders.map(item => fetchProviderStatus(item.provider)));
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          logApiError({ page: 'Integrations', operation: 'providerStatus', error: result.reason });
          setProviderStatuses(current => ({ ...current, [availableProviders[index].provider]: { connection: null } }));
        }
      });
    });
  }, [authConfig, backendHost, fetchProviderStatus, runRequest, selectedProviderId]);

  useEffect(() => {
    if (!checkLogin()) {
      navigate('/signin');
      return;
    }
    logEvent({ event: { label: 'PageView', data: { page: 'Integrations', user_id: orgMember?.id || null } } });
  }, [checkLogin, navigate, orgMember]);

  useEffect(() => {
    if (jwtToken) fetchProviders();
  }, [fetchProviders, jwtToken]);

  useEffect(() => {
    if (selectedProviderId) logSectionView('Integrations', selectedProviderId);
  }, [selectedProviderId]);

  useEffect(() => {
    if (!initialOAuthResult || !orgMember?.organization_id) return;
    if (!initialOAuthResult.status.endsWith('_success') && !initialOAuthResult.status.endsWith('_error')) return;
    returnToOnboarding({ organizationId: orgMember.organization_id, provider: initialOAuthResult.provider,
      kind: 'crm', success: initialOAuthResult.status.endsWith('_success') });
  }, [initialOAuthResult, orgMember?.organization_id]);

  function selectProvider(provider) {
    setSelectedProviderId(provider);
    const url = new URL(window.location.href);
    url.searchParams.set('provider', provider);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  async function connectProvider() {
    const url = new URL(window.location.href);
    ['oauth', 'code', 'message', 'provider_error', 'provider_description', 'correlation_id'].forEach(key => url.searchParams.delete(key));
    url.searchParams.set('provider', selectedProviderId);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    setOAuthResult(null);
    const result = await runRequest('connect', async () => {
      const response = await axios.get(`${backendHost}/v2/integrations/${selectedProviderId}/oauth/start`, authConfig);
      return response.data;
    });
    if (result?.authorizationUrl) {
      rememberOnboardingOAuth({ onboarding: embedded, organizationId: orgMember.organization_id, provider: selectedProviderId, kind: 'crm' });
      window.location.assign(result.authorizationUrl);
    }
  }

  async function disconnectProvider() {
    setLoadingAction('disconnect');
    setErrorMessage('');
    setActivityMessage('');
    try {
      const response = await axios.post(
        `${backendHost}/v2/integrations/${selectedProviderId}/oauth/disconnect`,
        { connection_id: connection?.id },
        authConfig,
      );
      const pausedCount = response.data.pausedWorkflows?.length || 0;
      const effects = [
        pausedCount ? `${pausedCount} associated workflow${pausedCount === 1 ? '' : 's'} automatically deactivated` : '',
      ].filter(Boolean);
      setActivityMessage(`${selectedProvider?.displayName || 'CRM'} disconnected.${effects.length ? ` ${effects.join(' and ')}.` : ''}`);
      await fetchProviderStatus(selectedProviderId);
    } catch (error) {
      logApiError({ page: 'Integrations', operation: 'disconnect', error });
      const apiError = error.response?.data?.error;
      setErrorMessage(apiError?.message || 'The CRM connection could not be disconnected.');
    } finally {
      setLoadingAction('');
    }
  }

  if (!jwtToken) return <div className="page-content crm-integrations-page" />;
  const oauthErrorMessage = oauthResult?.message || OAUTH_ERROR_MESSAGES[oauthResult?.code] || `${oauthProviderName} could not complete the connection. Start a new attempt.`;

  return (
    <div className={`page-content crm-integrations-page${embedded ? ' crm-integrations-embedded' : ''}`}>
      {!embedded && <PageHeader eyebrow="Integrations" title="Connect your business tools" description="Manage the calendars and customer systems Sayable.ai can use on your behalf." />}
      {errorMessage ? <div className="crm-notice error" role="alert">{errorMessage}</div> : null}
      {activityMessage ? <div className="crm-notice success" role="status">{activityMessage}</div> : null}
      {oauthIsSuccess ? <div className="crm-notice success">{oauthProviderName} connected successfully. It is now available to receptionist workflows.</div> : null}
      {oauthIsError ? <div className="crm-notice error crm-oauth-error" role="alert"><strong>{oauthProviderName} connection failed</strong><p>{oauthErrorMessage}</p>{oauthResult.providerError ? <p><strong>Provider error:</strong> {oauthResult.providerError}</p> : null}{oauthResult.providerDescription && !oauthErrorMessage.includes(oauthResult.providerDescription) ? <p><strong>Provider detail:</strong> {oauthResult.providerDescription}</p> : null}{oauthResult.correlationId ? <p className="crm-correlation">Reference: {oauthResult.correlationId}</p> : null}<p>The failed attempt is closed and will not block a new one.</p>{selectedProviderId ? <button className="crm-primary-button" onClick={connectProvider} disabled={!isAdmin || loadingAction === 'connect'}><BusyContent busy={loadingAction === 'connect'} label={"Starting new attempt…"}>{`Try ${oauthProviderName} again`}</BusyContent></button> : null}</div> : null}
      {!isAdmin ? <div className="crm-notice">Only organization admins can modify CRM integrations.</div> : null}

      {!embedded && <CalendarIntegrations organization={organization || orgMember?.organization} isAdmin={isAdmin} />}

      <section className="crm-provider-section" aria-labelledby="provider-heading"><div className="crm-section-intro"><div className="crm-provider-section-copy"><span className="say-eyebrow">CRM</span><h2 id="provider-heading">Choose a service</h2></div><p>Connections are reusable across multiple receptionist workflows.</p></div><div className="crm-provider-grid">{providers.map(provider => { const providerStatus = providerStatuses[provider.provider]; const connected = providerStatus?.connection?.status === 'connected'; const selected = selectedProviderId === provider.provider; return <button type="button" key={provider.provider} className={`crm-provider-card ${selected ? 'selected' : ''}`} onClick={() => selectProvider(provider.provider)} aria-pressed={selected}><ProviderMark provider={provider.provider} /><span className="crm-provider-card-copy"><strong>{provider.displayName}</strong><small>{PROVIDER_CONTENT[provider.provider]?.eyebrow || 'CRM integration'}</small></span><span className={`crm-card-status ${connected ? 'connected' : provider.available === false ? 'unavailable' : ''}`}>{connected ? 'Connected' : provider.available === false ? 'Unavailable' : 'Not connected'}</span><span className="crm-card-arrow" aria-hidden="true">›</span></button>; })}{!providers.length && loadingAction === 'providers' ? <LoadingState label="Loading integrations…" /> : null}</div></section>

      {selectedProvider ? <section className="crm-provider-workspace"><div className="crm-workspace-hero"><div className="crm-workspace-title"><ProviderMark provider={selectedProvider.provider} /><div><span>{providerContent.eyebrow}</span><h2>{selectedProvider.displayName}</h2><p>{selectedProvider.description}</p></div></div><span className={`crm-status-pill ${isConnected ? 'connected' : 'inactive'}`}>{isConnected ? 'Connected' : selectedProvider.available === false ? 'Unavailable' : 'Not connected'}</span></div><div className="crm-setup-grid"><div className="crm-setup-instructions"><h3>How connection works</h3><ol>{providerContent.steps.map(step => <li key={step}>{step}</li>)}</ol><p className="crm-security-note">Your CRM password is never shared with Sayable.ai. Access can be revoked at any time.</p></div><div className="crm-connection-panel"><div className="crm-section-heading"><h3>Connected account</h3><button type="button" className="crm-text-button" onClick={() => fetchProviderStatus(selectedProviderId)}>Refresh status</button></div>{status?.connections?.filter(item => item.status === 'connected').length > 1 ? <label className="crm-account-selector">Account<select value={connection?.id || ''} onChange={event => setSelectedConnectionIds(current => ({ ...current, [selectedProviderId]: event.target.value }))}>{status.connections.filter(item => item.status === 'connected').map(item => <option key={item.id} value={item.id}>{item.crm_org_name || item.crm_org_id || item.crm_user_id || item.id}</option>)}</select></label> : null}<dl className="crm-detail-list"><div><dt>Account</dt><dd>{connection?.crm_org_name || connection?.crm_org_id || 'Not connected'}</dd></div><div><dt>Authorized user</dt><dd>{connection?.crm_user_id || 'Unavailable'}</dd></div>{status?.connections?.filter(item => item.status === 'connected').length > 1 ? <div className="wide"><dt>Connected accounts</dt><dd>{status.connections.filter(item => item.status === 'connected').length} {selectedProvider.displayName} accounts are connected; workflows target a specific account.</dd></div> : null}</dl><div className="crm-actions"><button type="button" className="crm-primary-button" onClick={connectProvider} disabled={!isAdmin || selectedProvider.available === false || loadingAction === 'connect'}><BusyContent busy={loadingAction === 'connect'} label={"Opening secure connection…"}>{isConnected ? `Reconnect ${selectedProvider.displayName}` : `Connect ${selectedProvider.displayName}`}</BusyContent></button>{isConnected ? <button type="button" className="crm-secondary-button danger" onClick={disconnectProvider} disabled={!isAdmin || loadingAction === 'disconnect'}><BusyContent busy={loadingAction === 'disconnect'} label={"Disconnecting and deactivating…"}>{'Disconnect and deactivate workflows'}</BusyContent></button> : null}</div></div></div>{isConnected && !embedded ? <div className="crm-workflow-handoff"><div><div><h3>Use this account in a receptionist workflow</h3><p>Create one or more intent-driven paths. Each path owns its phone questions, mappings, and CRM outcome.</p></div></div><Link className="crm-primary-button" to="/receptionist#workflows">Manage receptionist workflows →</Link></div> : null}</section> : !embedded ? <EmptyState icon="↥" title="Select an integration to get started" description="Connection instructions and live account status will appear here." /> : null}
    </div>
  );
}

export default CRMIntegrations;
